import React, { useState, useEffect } from 'react';
import { placeAPI, cityAPI } from '../services/api';
import { Place, City } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  SelectChangeEvent,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Edit, Delete, Refresh, Add, LocationOn, Star, Visibility, Search } from '@mui/icons-material';
import TranslationInput from '../components/TranslationInput';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const Places: React.FC = () => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPlaceId, setEditingPlaceId] = useState<string | null>(null);
  const [form, setForm] = useState({
    placeName: '',
    description: '',
    cityId: '',
    latitude: '',
    longitude: '',
    placeType: '',
    address: '',
    contactNumber: '',
    operatingHours: '',
    website: '',
    email: '',
    category: '',
    subCategory: '',
    isWheelchairAccessible: false,
    hasParking: false,
    hasPublicTransport: false,
    isActive: true
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Place types and categories
  const placeTypes = [
    'LANDMARK', 'MALL', 'HOSPITAL', 'UNIVERSITY', 'PARK', 'AIRPORT',
    'RAILWAY_STATION', 'BUS_STAND', 'HOTEL', 'RESTAURANT', 'MUSEUM',
    'LIBRARY', 'STADIUM', 'THEATER', 'CHURCH', 'TEMPLE', 'MOSQUE', 'OTHER'
  ];

  const categories = [
    'TOURIST_ATTRACTION', 'SHOPPING', 'HEALTHCARE', 'EDUCATION',
    'ENTERTAINMENT', 'RELIGIOUS', 'TRANSPORT', 'ACCOMMODATION',
    'DINING', 'RECREATION', 'OTHER'
  ];

  useEffect(() => {
    fetchPlaces();
    fetchCities();
  }, []);

  const fetchPlaces = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await placeAPI.getAll();
      setPlaces(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load places');
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await cityAPI.getAll();
      setCities(res.data);
    } catch (e: unknown) {
      console.error('Failed to load cities:', e);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError('');
  };

  const handleSelectInput = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    setForm({ ...form, [name]: e.target.value });
    setFormError('');
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.checked });
    setFormError('');
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setEditingPlaceId(null);
    setForm({
      placeName: '',
      description: '',
      cityId: '',
      latitude: '',
      longitude: '',
      placeType: '',
      address: '',
      contactNumber: '',
      operatingHours: '',
      website: '',
      email: '',
      category: '',
      subCategory: '',
      isWheelchairAccessible: false,
      hasParking: false,
      hasPublicTransport: false,
      isActive: true
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (place: Place) => {
    setIsEditMode(true);
    setEditingPlaceId(place.id!);
    setForm({
      placeName: place.placeName,
      description: place.description || '',
      cityId: place.cityId,
      latitude: place.latitude?.toString() || '',
      longitude: place.longitude?.toString() || '',
      placeType: place.placeType,
      address: place.address || '',
      contactNumber: place.contactNumber || '',
      operatingHours: place.operatingHours || '',
      website: place.website || '',
      email: place.email || '',
      category: place.category || '',
      subCategory: place.subCategory || '',
      isWheelchairAccessible: place.isWheelchairAccessible || false,
      hasParking: place.hasParking || false,
      hasPublicTransport: place.hasPublicTransport || false,
      isActive: place.isActive !== false
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setEditingPlaceId(null);
    setForm({
      placeName: '',
      description: '',
      cityId: '',
      latitude: '',
      longitude: '',
      placeType: '',
      address: '',
      contactNumber: '',
      operatingHours: '',
      website: '',
      email: '',
      category: '',
      subCategory: '',
      isWheelchairAccessible: false,
      hasParking: false,
      hasPublicTransport: false,
      isActive: true
    });
    setFormError('');
  };

  const handleSavePlace = async () => {
    if (!form.placeName.trim() || !form.cityId || !form.latitude.trim() || !form.longitude.trim() || !form.placeType) {
      setFormError('Place name, city, coordinates, and place type are required.');
      return;
    }
    
    // Validate coordinates
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setFormError('Invalid coordinates. Latitude must be between -90 and 90, longitude between -180 and 180.');
      return;
    }

    // Validate email if provided
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setFormError('Invalid email format.');
      return;
    }

    // Validate website if provided
    if (form.website && !/^https?:\/\/.+/.test(form.website)) {
      setFormError('Website must start with http:// or https://');
      return;
    }

    setSaving(true);
    try {
      const placeData = {
        placeName: form.placeName.trim(),
        description: form.description.trim() || undefined,
        cityId: form.cityId,
        latitude: lat,
        longitude: lng,
        placeType: form.placeType,
        address: form.address.trim() || undefined,
        contactNumber: form.contactNumber.trim() || undefined,
        operatingHours: form.operatingHours.trim() || undefined,
        website: form.website.trim() || undefined,
        email: form.email.trim() || undefined,
        category: form.category || undefined,
        subCategory: form.subCategory.trim() || undefined,
        isWheelchairAccessible: form.isWheelchairAccessible,
        hasParking: form.hasParking,
        hasPublicTransport: form.hasPublicTransport,
        isActive: form.isActive
      };

      if (isEditMode && editingPlaceId) {
        await placeAPI.update(editingPlaceId, placeData);
        setSuccessMsg('Place updated successfully!');
      } else {
        await placeAPI.create(placeData);
        setSuccessMsg('Place created successfully!');
      }
      closeModal();
      fetchPlaces();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || `Failed to ${isEditMode ? 'update' : 'create'} place`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this place?')) return;
    
    setDeletingId(id);
    try {
      await placeAPI.delete(id);
      setSuccessMsg('Place deleted successfully!');
      fetchPlaces();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete place');
    } finally {
      setDeletingId(null);
    }
  };

  const getCityName = (cityId: string) => {
    const city = cities.find(c => c.id === cityId);
    return city ? city.name : cityId;
  };

  const getPlaceTypeColor = (placeType: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default' } = {
      'LANDMARK': 'primary',
      'MALL': 'secondary',
      'HOSPITAL': 'error',
      'UNIVERSITY': 'info',
      'PARK': 'success',
      'AIRPORT': 'warning',
      'RAILWAY_STATION': 'primary',
      'BUS_STAND': 'secondary',
      'HOTEL': 'info',
      'RESTAURANT': 'warning',
      'MUSEUM': 'primary',
      'LIBRARY': 'info',
      'STADIUM': 'success',
      'THEATER': 'secondary',
      'CHURCH': 'primary',
      'TEMPLE': 'primary',
      'MOSQUE': 'primary',
      'OTHER': 'default'
    };
    return colors[placeType] || 'default';
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Places Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchPlaces}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={openAddModal}
            sx={{ borderRadius: 2 }}
          >
            Add Place
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Coordinates</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Features</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {places.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No places found.</TableCell>
                  </TableRow>
                ) : places.map(place => (
                  <TableRow key={place.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {place.placeName}
                        </Typography>
                        {place.description && (
                          <Typography variant="caption" color="text.secondary">
                            {place.description.length > 50 
                              ? `${place.description.substring(0, 50)}...` 
                              : place.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={getCityName(place.cityId)} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={place.placeType} 
                        size="small" 
                        color={getPlaceTypeColor(place.placeType)} 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {place.category ? (
                        <Chip label={place.category} size="small" color="info" variant="outlined" />
                      ) : (
                        <Chip label="Not set" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`${place.latitude}, ${place.longitude}`}>
                        <Chip 
                          icon={<LocationOn />}
                          label={`${place.latitude.toFixed(4)}, ${place.longitude.toFixed(4)}`} 
                          size="small" 
                          color="secondary" 
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {place.address ? (
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {place.address.length > 30 
                            ? `${place.address.substring(0, 30)}...` 
                            : place.address}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Not set</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {place.isWheelchairAccessible && (
                          <Chip label="♿" size="small" color="success" variant="outlined" />
                        )}
                        {place.hasParking && (
                          <Chip label="P" size="small" color="info" variant="outlined" />
                        )}
                        {place.hasPublicTransport && (
                          <Chip label="🚌" size="small" color="warning" variant="outlined" />
                        )}
                        {place.rating && (
                          <Chip 
                            icon={<Star />}
                            label={place.rating.toFixed(1)} 
                            size="small" 
                            color="warning" 
                            variant="outlined" 
                          />
                        )}
                        {place.visitCount && place.visitCount > 0 && (
                          <Chip 
                            icon={<Visibility />}
                            label={place.visitCount} 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={place.isActive ? 'Active' : 'Inactive'} 
                        size="small" 
                        color={place.isActive ? 'success' : 'error'} 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit Place">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEditModal(place)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Place">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(place.id!)}
                            disabled={deletingId === place.id}
                          >
                            {deletingId === place.id ? <CircularProgress size={16} /> : <Delete />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={showModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Place' : 'Add New Place'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TranslationInput
                label="Place Name"
                value={form.placeName}
                onChange={(value) => setForm({ ...form, placeName: value })}
                entityType="PLACE"
                entityId={isEditMode && editingPlaceId ? editingPlaceId : 'new'}
                fieldName="placeName"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="Description" 
                name="description" 
                value={form.description} 
                onChange={handleInput} 
                fullWidth 
                multiline
                rows={3}
                placeholder="Brief description of the place"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>City</InputLabel>
                <Select
                  name="cityId"
                  value={form.cityId}
                  onChange={handleSelectInput}
                  label="City"
                >
                  {cities.map(city => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name} ({city.networkId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Place Type</InputLabel>
                <Select
                  name="placeType"
                  value={form.placeType}
                  onChange={handleSelectInput}
                  label="Place Type"
                >
                  {placeTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Latitude" 
                name="latitude" 
                value={form.latitude} 
                onChange={handleInput} 
                fullWidth 
                required
                placeholder="e.g., 28.6139"
                helperText="Required: Latitude (-90 to 90)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Longitude" 
                name="longitude" 
                value={form.longitude} 
                onChange={handleInput} 
                fullWidth 
                required
                placeholder="e.g., 77.2090"
                helperText="Required: Longitude (-180 to 180)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="Address" 
                name="address" 
                value={form.address} 
                onChange={handleInput} 
                fullWidth 
                placeholder="Full address of the place"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Contact Number" 
                name="contactNumber" 
                value={form.contactNumber} 
                onChange={handleInput} 
                fullWidth 
                placeholder="e.g., +91-9876543210"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Operating Hours" 
                name="operatingHours" 
                value={form.operatingHours} 
                onChange={handleInput} 
                fullWidth 
                placeholder="e.g., 09:00-21:00"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Website" 
                name="website" 
                value={form.website} 
                onChange={handleInput} 
                fullWidth 
                placeholder="https://example.com"
                helperText="Must start with http:// or https://"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Email" 
                name="email" 
                value={form.email} 
                onChange={handleInput} 
                fullWidth 
                placeholder="info@example.com"
                helperText="Valid email format"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={form.category}
                  onChange={handleSelectInput}
                  label="Category"
                >
                  <MenuItem value="">Select category (optional)</MenuItem>
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Sub Category" 
                name="subCategory" 
                value={form.subCategory} 
                onChange={handleInput} 
                fullWidth 
                placeholder="More specific categorization"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Accessibility & Features
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isWheelchairAccessible}
                      onChange={handleSwitchChange}
                      name="isWheelchairAccessible"
                    />
                  }
                  label="Wheelchair Accessible"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.hasParking}
                      onChange={handleSwitchChange}
                      name="hasParking"
                    />
                  }
                  label="Has Parking"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.hasPublicTransport}
                      onChange={handleSwitchChange}
                      name="hasPublicTransport"
                    />
                  }
                  label="Public Transport Access"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={handleSwitchChange}
                      name="isActive"
                    />
                  }
                  label="Active"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSavePlace} 
            variant="contained" 
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {saving ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Places;
